import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrashIcon, MinusIcon, PlusIcon, ShoppingBagIcon, TagIcon, ArrowLeftIcon, PackageIcon } from 'lucide-react';
import { useStore } from '../context/StoreContext';
export function CartPage() {
  const {
    cart,
    removeFromCart,
    updateQuantity,
    cartTotal,
    user,
    validatePromoCode,
    adminSettings
  } = useStore();
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponMessage, setCouponMessage] = useState('');
  const deliveryCharge = cartTotal >= 999 ? 0 : 49;
  const totalSavings = cart.reduce((sum, item) => sum + (item.product.mrp - item.product.sellingPrice) * item.quantity, 0);
  const finalTotal = cartTotal - couponDiscount + deliveryCharge;
  const handleApplyCoupon = () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    const result = validatePromoCode(couponCode, cart);
    if (result.isValid) {
      setCouponApplied(true);
      setCouponDiscount(result.discount);
      setCouponMessage(result.message);
      setCouponError('');
    } else {
      setCouponApplied(false);
      setCouponDiscount(0);
      setCouponError(result.message);
      setCouponMessage('');
    }
  };
  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponApplied(false);
    setCouponDiscount(0);
    setCouponMessage('');
    setCouponError('');
  };
  const handleCheckout = () => {
    if (!user) {
      navigate('/login');
    } else {
      navigate('/checkout');
    }
  };
  if (cart.length === 0) {
    return <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4 py-20" data-id="element-804">
        <motion.div className="text-center" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-805">
          <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-806">
            <ShoppingBagIcon className="w-10 h-10 text-slate-400" data-id="element-807" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-808">
            Your Cart is Empty
          </h2>
          <p className="text-slate-500 mb-8 max-w-md" data-id="element-809">
            Looks like you haven't added anything to your cart yet. Start
            shopping to fill it up!
          </p>
          <Link to="/products" className="inline-flex items-center gap-2 px-8 py-3 bg-sky-600 text-white font-semibold rounded-lg hover:bg-sky-700 transition-colors" data-id="element-810">
            <ShoppingBagIcon className="w-5 h-5" data-id="element-811" /> Start Shopping
          </Link>
        </motion.div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50" data-id="element-812">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8" data-id="element-813">
        <div className="flex items-center gap-4 mb-6" data-id="element-814">
          <Link to="/products" className="p-2 text-slate-500 hover:text-sky-600 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-815">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-816" />
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" data-id="element-817">
            Shopping Cart{' '}
            <span className="text-slate-400 text-lg font-normal" data-id="element-818">
              ({cart.length} items)
            </span>
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-819">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4" data-id="element-820">
            {cart.map((item, index) => <motion.div key={`${item.product.id}-${item.size}`} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.05
          }} className="bg-white rounded-xl border border-slate-200 p-4 flex gap-4" data-id="element-821">
                <Link to={`/product/${item.product.id}`} className="flex-shrink-0" data-id="element-822">
                  <img src={item.product.images[0]} alt={item.product.name} className="w-24 h-28 md:w-28 md:h-32 object-cover rounded-lg bg-slate-100" data-id="element-823" />
                </Link>
                <div className="flex-1 min-w-0" data-id="element-824">
                  <Link to={`/product/${item.product.id}`} className="hover:text-sky-600 transition-colors" data-id="element-825">
                    <h3 className="font-medium text-slate-900 text-sm md:text-base line-clamp-2" data-id="element-826">
                      {item.product.name}
                    </h3>
                  </Link>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-827">
                    {item.product.category}
                  </p>
                  <span className="inline-block mt-1 text-xs font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded" data-id="element-828">
                    Size: {item.size}
                  </span>

                  <div className="flex items-center justify-between mt-3" data-id="element-829">
                    <div className="flex items-center gap-2" data-id="element-830">
                      <span className="font-bold text-slate-900" data-id="element-831">
                        ₹{item.product.sellingPrice}
                      </span>
                      {item.product.mrp > item.product.sellingPrice && <span className="text-xs text-slate-400 line-through" data-id="element-832">
                          ₹{item.product.mrp}
                        </span>}
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-3" data-id="element-833">
                    <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden" data-id="element-834">
                      <button onClick={() => updateQuantity(item.product.id, item.size, item.quantity - 1)} className="p-2 hover:bg-slate-50 transition-colors text-slate-600" aria-label="Decrease quantity" data-id="element-835">
                        <MinusIcon className="w-4 h-4" data-id="element-836" />
                      </button>
                      <span className="px-4 py-1 text-sm font-medium text-slate-900 min-w-[40px] text-center" data-id="element-837">
                        {item.quantity}
                      </span>
                      <button onClick={() => updateQuantity(item.product.id, item.size, item.quantity + 1)} className="p-2 hover:bg-slate-50 transition-colors text-slate-600" aria-label="Increase quantity" data-id="element-838">
                        <PlusIcon className="w-4 h-4" data-id="element-839" />
                      </button>
                    </div>
                    <button onClick={() => removeFromCart(item.product.id, item.size)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" aria-label="Remove item" data-id="element-840">
                      <TrashIcon className="w-4 h-4" data-id="element-841" />
                    </button>
                  </div>
                </div>
              </motion.div>)}
          </div>

          {/* Price Summary */}
          <div className="lg:col-span-1" data-id="element-842">
            <div className="bg-white rounded-xl border border-slate-200 p-6 sticky top-24" data-id="element-843">
              <h3 className="font-bold text-slate-900 mb-4 text-lg" data-id="element-844">
                Order Summary
              </h3>

              {/* Coupon */}
              {adminSettings.promoEnabled && <div className="mb-6 pb-6 border-b border-slate-100" data-id="element-845">
                  <div className="flex gap-2" data-id="element-846">
                    <div className="flex-1 relative" data-id="element-847">
                      <TagIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-848" />
                      <input type="text" placeholder="Coupon code" value={couponCode} onChange={e => {
                    setCouponCode(e.target.value);
                    setCouponError('');
                  }} disabled={couponApplied} className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-500" data-id="element-849" />
                    </div>
                    {couponApplied ? <button onClick={handleRemoveCoupon} className="px-4 py-2.5 bg-red-50 text-red-600 text-sm font-medium rounded-lg hover:bg-red-100 transition-colors" data-id="element-850">
                        Remove
                      </button> : <button onClick={handleApplyCoupon} className="px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors" data-id="element-851">
                        Apply
                      </button>}
                  </div>
                  {couponError && <p className="text-red-500 text-xs mt-2" data-id="element-852">{couponError}</p>}
                  {couponApplied && <p className="text-green-600 text-xs mt-2 font-medium" data-id="element-853">
                      {couponMessage}
                    </p>}
                </div>}

              <div className="space-y-3 text-sm" data-id="element-854">
                <div className="flex justify-between text-slate-600" data-id="element-855">
                  <span data-id="element-856">
                    Subtotal ({cart.reduce((s, i) => s + i.quantity, 0)} items)
                  </span>
                  <span data-id="element-857">₹{cartTotal}</span>
                </div>
                {totalSavings > 0 && <div className="flex justify-between text-green-600" data-id="element-858">
                    <span data-id="element-859">MRP Savings</span>
                    <span data-id="element-860">-₹{totalSavings}</span>
                  </div>}
                {couponApplied && <div className="flex justify-between text-green-600 font-medium" data-id="element-861">
                    <span data-id="element-862">Coupon Discount</span>
                    <span data-id="element-863">-₹{couponDiscount}</span>
                  </div>}
                <div className="flex justify-between text-slate-600" data-id="element-864">
                  <span data-id="element-865">Delivery</span>
                  <span className={deliveryCharge === 0 ? 'text-green-600 font-medium' : ''} data-id="element-866">
                    {deliveryCharge === 0 ? 'FREE' : `₹${deliveryCharge}`}
                  </span>
                </div>
                {deliveryCharge > 0 && <p className="text-xs text-slate-400" data-id="element-867">
                    Free delivery on orders above ₹999
                  </p>}
                <div className="flex justify-between font-bold text-slate-900 text-base pt-3 border-t border-slate-200" data-id="element-868">
                  <span data-id="element-869">Total</span>
                  <span data-id="element-870">₹{finalTotal}</span>
                </div>
              </div>

              <button onClick={handleCheckout} className="w-full mt-6 py-3.5 bg-sky-600 text-white font-semibold rounded-xl hover:bg-sky-700 transition-colors shadow-md shadow-sky-200 flex items-center justify-center gap-2" data-id="element-871">
                <PackageIcon className="w-5 h-5" data-id="element-872" />
                Proceed to Checkout
              </button>

              <Link to="/products" className="block text-center mt-3 text-sm text-sky-600 hover:text-sky-700 font-medium" data-id="element-873">
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>;
}